import { useState, useEffect, useRef, useCallback } from 'react'

// Native Logic and Controls will NOT always play nice together
// Ex: Chrome Video Controls & onClick event will counter act each other
// You must Decide which one you want to use
// Taking care of this ISSUE with CSS and `customControls` prop
type Props = {
  file: string
  customControls: boolean
}

type VideoState = 'initial' | 'loading' | 'ready'

type VideoFileData = {
  videoPosition?: number
  videoDuration?: number
  videoHeight?: number
  videoWidth?: number
  videoTime?: string
  videoDimensions?: string
  videoAspectRatio?: string
}

type VideoPlayerData = VideoFileData & {
  isStopped: boolean
  videoFile: string
  videoState: VideoState
  videoRef: (node: HTMLVideoElement) => void
  videoElement: HTMLVideoElement | null
}

const useVideoPlayer = (data: Props): VideoPlayerData => {
  const { file, customControls } = data
  const [videoElement, handleVideoElement] = useState<HTMLVideoElement | null>(null)
  const [isStopped, setIsStopped] = useState<boolean>(true)
  const [videoFile, handleVideoFile] = useState<string>(file)
  const [videoData, handleVideoData] = useState<VideoFileData>({})
  const [videoState, handleVideoState] = useState<VideoState>('initial')
  const [videoClickCount, handleVideClickCount] = useState<number>(0)

  const videoRef = useCallback((node: HTMLVideoElement) => {
    if (node !== null) {
      handleVideoElement(node)
    }
  }, [])

  useEffect(() => {
    // Functions Below are all NATIVE calls
    // Issue that initial EVENT maybe fired before `useEffect` happens
    // We need to add INITIAL logic to `video` tag
    // These will catch file changes ( new FILE )
    if (videoElement) {
      handleVideoState('loading')
      handleVideoFile(videoFile)
      videoElement.ontimeupdate = (event) => {
        updateVideoData()
      }
      videoElement.oncanplay = (event) => {
        handleVideoState('ready')
      }
      videoElement.onloadedmetadata = (event) => {
        handleVideoState('ready')
      }
      videoElement.onloadeddata = (event) => {
        handleVideoState('ready')
      }
      videoElement.onloadstart = (event) => {
        setIsStopped(true)
        handleVideoState('loading')
      }
      videoElement.onplay = () => {
        setIsStopped(false)
        handleVideoState('ready')
      }
      videoElement.onplaying = () => {
        setIsStopped(false)
        handleVideoState('ready')
      }
      videoElement.onpause = () => {
        setIsStopped(true)
      }
      videoElement.oncontextmenu = (ev: MouseEvent) => {
        ev.preventDefault()
        return
      }
      // videoElement.ondblclick = () => {
      //   console.log('doubleClick')
      // }
      // State is GETTING cached inside of this
      // Meaning can't Change Play/Pause state
      if (customControls) {
        videoElement.onclick = () => handleVideClickCount((prevState: number) => prevState + 1)
      }
    }
  }, [videoElement])

  useEffect(() => {
    if (!videoElement) return
    // How we RESOLVE initial LOAD and FILE changes
    videoElement.load()
  }, [file, videoElement])

  useEffect(() => {
    if (!videoElement) return
    // How we RESOLVE initial LOAD of VIDEO file for showing TIME/DIMENSIONS
    if (videoState === 'ready') {
      updateVideoData()
    }
  }, [videoState])

  useEffect(() => {
    togglePlayPause()
  }, [videoClickCount])

  const updateVideoData = () => {
    if (!videoElement) return
    const { currentTime, duration, videoHeight, videoWidth } = videoElement
    const videoPosition = Math.round(currentTime)
    const videoDuration = Math.round(duration)
    const videoTime = `${formatSeconds(videoPosition)} / ${formatSeconds(videoDuration)}`
    handleVideoData({
      videoPosition,
      videoDuration,
      videoHeight,
      videoWidth,
      videoTime,
      videoAspectRatio: `${videoWidth} / ${videoHeight}`,
      videoDimensions: `${videoWidth} x ${videoHeight}`,
    })
  }

  const togglePlayPause = () => {
    if (!videoElement) return
    if (isStopped) {
      videoElement.play()
    } else {
      videoElement.pause()
    }
  }

  const formatSeconds = (totalSeconds: number): string => {
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const formattedMinutes = String(minutes).padStart(2, '0')
    const formattedSeconds = String(seconds).padStart(2, '0')

    return `${formattedMinutes}:${formattedSeconds}`
  }

  return {
    ...videoData,
    isStopped,
    videoFile,
    videoRef,
    videoState,
    videoElement,
  }
}

export default useVideoPlayer
